import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { todayISO } from "../lib/format";

const INCOME_CATS = ["Satış Geliri", "Danışmanlık", "Faiz Geliri", "Diğer Gelir"];
const EXPENSE_CATS = [
  "Ofis & Kira",
  "Personel",
  "Yazılım & Abonelik",
  "Vergi & Sigorta",
  "Pazarlama",
  "Kargo & Lojistik",
  "Diğer Gider",
];

export default function TransactionModal({
  open,
  onClose,
  onSave,
  accounts = [],
  contacts = [],
  defaultType = "income",
  initial = null,
}) {
  const [form, setForm] = useState({
    type: defaultType,
    description: "",
    category: INCOME_CATS[0],
    amount: "",
    account_id: "",
    contact_id: "",
    date: todayISO(),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isEdit = !!initial;

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          type: initial.type,
          description: initial.description || "",
          category: initial.category || INCOME_CATS[0],
          amount: String(initial.amount ?? ""),
          account_id: initial.account_id || "",
          contact_id: initial.contact_id || "",
          date: (initial.date || todayISO()).slice(0, 10),
        });
      } else {
        setForm({
          type: defaultType,
          description: "",
          category: defaultType === "income" ? INCOME_CATS[0] : EXPENSE_CATS[0],
          amount: "",
          account_id: accounts[0]?.id || "",
          contact_id: "",
          date: todayISO(),
        });
      }
      setError("");
    }
  }, [open, initial, defaultType, accounts]);

  if (!open) return null;

  const setType = (t) =>
    setForm((f) => ({ ...f, type: t, category: t === "income" ? INCOME_CATS[0] : EXPENSE_CATS[0] }));

  const submit = async () => {
    if (!form.description.trim()) return setError("Açıklama gerekli");
    const amt = Number(form.amount);
    if (!amt || amt <= 0) return setError("Geçerli bir tutar girin");
    setSaving(true);
    setError("");
    try {
      const account = accounts.find((a) => a.id === form.account_id);
      const contact = contacts.find((c) => c.id === form.contact_id);
      await onSave({
        type: form.type,
        description: form.description.trim(),
        category: form.category,
        amount: amt,
        account_id: form.account_id || null,
        account: account ? `${account.bank} · ${account.last_four}` : null,
        contact_id: form.contact_id || null,
        contact: contact ? contact.name : null,
        date: form.date,
      });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setSaving(false);
    }
  };

  const cats = form.type === "income" ? INCOME_CATS : EXPENSE_CATS;

  return (
    <div className="modal-backdrop" data-testid="transaction-modal" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">{isEdit ? "DÜZENLE" : "YENİ KAYIT"}</span>
            <h2>{isEdit ? "İşlemi düzenle" : "İşlem ekle"}</h2>
          </div>
          <button className="icon-btn" onClick={onClose} data-testid="close-transaction-modal">
            <X size={18} />
          </button>
        </div>

        <div className="segmented">
          <button
            className={form.type === "income" ? "active" : ""}
            onClick={() => setType("income")}
            data-testid="transaction-type-income"
          >
            Gelir
          </button>
          <button
            className={form.type === "expense" ? "active" : ""}
            onClick={() => setType("expense")}
            data-testid="transaction-type-expense"
          >
            Gider
          </button>
        </div>

        <label>
          Açıklama
          <input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Örn. Proje tahsilatı"
            data-testid="transaction-description-input"
          />
        </label>

        <div className="form-grid">
          <label>
            Tutar (₺)
            <input
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="0"
              data-testid="transaction-amount-input"
            />
          </label>
          <label>
            Tarih
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              data-testid="transaction-date-input"
            />
          </label>
        </div>

        <label>
          Kategori
          <select
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            data-testid="transaction-category-select"
          >
            {cats.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>

        <div className="form-grid">
          <label>
            Hesap
            <select
              value={form.account_id}
              onChange={(e) => setForm({ ...form, account_id: e.target.value })}
              data-testid="transaction-account-select"
            >
              <option value="">Seçilmedi</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.bank} · {a.last_four}
                </option>
              ))}
            </select>
          </label>
          <label>
            Cari
            <select
              value={form.contact_id}
              onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
              data-testid="transaction-contact-select"
            >
              <option value="">Seçilmedi</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error ? <div className="form-error" data-testid="transaction-error">{error}</div> : null}

        <button className="primary-btn full" onClick={submit} disabled={saving} data-testid="save-transaction-button">
          <Plus size={17} /> {saving ? "Kaydediliyor…" : isEdit ? "Güncelle" : "İşlemi kaydet"}
        </button>
      </div>
    </div>
  );
}
